/**
 * @fileoverview WEB-667 / DPL-17 — Discovery v2 two-part results-bar count.
 *
 * Copy is final per the developer handoff. Do not paraphrase the labels.
 *
 *   Full (≥721px): `14 in range Tonight · 5 match your taste`
 *   Slim (≤720px): `14 Tonight · 5 match`
 *
 * The taste clause is omitted (never coerced to `0`) when
 * `tasteMatchedTotal` is `null` — that is the FRE-917 gate miss / "not
 * measurable" wire value from API-1671. A real `0` is a measured zero and
 * MUST render.
 *
 * WEB-790 / PAR-25 — the trailing scope noun ("Tonight" above) tracks the
 * active `date` URL param instead of being a hardcoded literal. See
 * {@link formatDiscoverResultsScopeNoun}.
 *
 * WEB-946 / DES-79 — rail Category/Neighborhood heads and the scope caption
 * reuse this same map. Prefer `facets.scopeKey` (API-1746) when present so
 * chrome cannot name a window the API did not measure.
 */

import { formatCount } from '@/lib/discover/formatCount';
import { DATE_WINDOW_LABELS } from '@/lib/discover/heroMeta';
import type { DateValue } from '@/lib/discover/url-state';

export const RESULTS_COUNT_SLIM_MAX_PX = 720;

export type DiscoverResultsCountVariant = 'full' | 'slim';

/**
 * Date-window key shared by the URL `date` param and `facets.scopeKey`.
 * `'upcoming'` is API-only (omitted `?date=`).
 */
export type DiscoverScopeKey = DateValue | 'upcoming';

const ISO_DATE_RANGE = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/;

const UPCOMING_SCOPE_TOKEN = 'Upcoming';

/**
 * Scope noun trailing "IN RANGE" (WEB-790 / PAR-25). Reuses the hero strip's
 * `DATE_WINDOW_LABELS` (`lib/discover/heroMeta.ts`) so the two chrome
 * surfaces share one source of truth for what "next 30 days" reads as, rather
 * than two literal copies that could silently drift. A custom ISO range names
 * its own span. No date filter / `'upcoming'` — the default "all upcoming"
 * state — omits the noun rather than fabricating a window that was never
 * selected. The results bar copy is unchanged (WEB-946).
 */
export function formatDiscoverResultsScopeNoun(date: DiscoverScopeKey | undefined): string {
  if (date === undefined || date === 'upcoming') return '';
  const preset = DATE_WINDOW_LABELS[date] ?? '';
  if (preset !== '') return preset;
  if (ISO_DATE_RANGE.test(date)) {
    const [start, end] = date.split('..') as [string, string];
    return start === end ? start : `${start} to ${end}`;
  }
  return '';
}

/**
 * Prefer the API's measured `facets.scopeKey`. Fall back to the URL date
 * (mapped to `'upcoming'` when unset) while the envelope is still loading.
 */
export function resolveDiscoverScopeKey(
  facetsScopeKey: string | undefined,
  date: DateValue | undefined,
): DiscoverScopeKey {
  if (facetsScopeKey !== undefined && facetsScopeKey !== '') {
    return facetsScopeKey;
  }
  return date ?? 'upcoming';
}

/**
 * Scope token for rail heads — same vocabulary as the results bar,
 * plus `Upcoming` when the bar omits a noun (WEB-946).
 */
export function formatDiscoverScopeToken(scope: DiscoverScopeKey | undefined): string {
  return formatDiscoverResultsScopeNoun(scope) || UPCOMING_SCOPE_TOKEN;
}

/**
 * Lowercase `{dateLabel}` for the rail scope caption. Same token as the bar
 * and heads, sentence-cased into the existing
 * `{n} categories · {m} events {dateLabel} citywide` frame.
 */
export function formatDiscoverScopeCaptionPhrase(scope: DiscoverScopeKey | undefined): string {
  return formatDiscoverScopeToken(scope).toLowerCase();
}

/** Category head right side: `{SCOPE} · {CITY}` e.g. `Upcoming · CLT`. */
export function formatDiscoverCategoryHeadScope(
  scope: DiscoverScopeKey | undefined,
  cityCode: string,
): string {
  return `${formatDiscoverScopeToken(scope)} · ${cityCode}`;
}

/** Neighborhood head right side: `{SCOPE}` e.g. `Tonight`. */
export function formatDiscoverNeighborhoodHeadScope(scope: DiscoverScopeKey | undefined): string {
  return formatDiscoverScopeToken(scope);
}

export interface DiscoverResultsCountInput {
  /** Corpus-wide in-range total (`meta.total`). */
  readonly total: number;
  /**
   * Corpus-wide taste-matched total (`meta.tasteMatchedTotal`).
   * `null` = FRE-917 gate failed or not measurable — omit the taste clause.
   */
  readonly tasteMatchedTotal: number | null;
  readonly variant: DiscoverResultsCountVariant;
  /**
   * Active window for the bar noun (WEB-790 / PAR-25). Prefer the API's
   * `facets.scopeKey` mapped through {@link dateForDiscoverResultsBar} so
   * `'upcoming'` stays omitted (`N IN RANGE`) and named presets match the
   * rail heads. Omitted / `undefined` is the default "all upcoming" state.
   */
  readonly date?: DateValue;
}

/**
 * Results-bar `date` from a resolved scope key. `'upcoming'` maps to
 * `undefined` so {@link formatDiscoverResultsCount} keeps `N IN RANGE`
 * with no token (WEB-946 / WEB-790).
 */
export function dateForDiscoverResultsBar(
  scope: DiscoverScopeKey | undefined,
): DateValue | undefined {
  return scope === undefined || scope === 'upcoming' ? undefined : scope;
}

/**
 * Taste-clause copy shared by the results bar and the map-takeover drawer
 * (WEB-682). `null` means the FRE-917 gate missed — omit the clause, never
 * coerce to a fabricated zero. A real `0` is a measured zero and MUST render.
 *
 * FRE-1418 — the compact (`'slim'`) cell carries no taste clause: a bare
 * "N match" next to a number reads like a score, so the slim cell shows the
 * plain results count only. The wide wording is unchanged.
 */
export function formatDiscoverTasteClause(
  tasteMatchedTotal: number | null,
  variant: DiscoverResultsCountVariant = 'full',
): string | null {
  if (tasteMatchedTotal === null || variant === 'slim') {
    return null;
  }
  return `${formatCount(tasteMatchedTotal)} match your taste`;
}

/**
 * Format one variant of the two-part honest count. Both variants are
 * rendered in the DOM and toggled with CSS; this helper is the single
 * source of the literal strings so the bar, unit tests, and Playwright
 * cannot drift.
 */
export function formatDiscoverResultsCount(input: DiscoverResultsCountInput): string {
  const total = formatCount(input.total);
  const base = input.variant === 'full' ? `${total} in range` : total;
  const scopeNoun = formatDiscoverResultsScopeNoun(input.date);
  const inRange = [base, scopeNoun].filter(Boolean).join(' ');
  const taste = formatDiscoverTasteClause(input.tasteMatchedTotal, input.variant);
  return [inRange, taste].filter(Boolean).join(' · ');
}

/**
 * WEB-1074 — the label that follows the figure in the v3.1 context bar's count
 * cell, e.g. ` in range Tonight · 5 match your taste`. The bar paints the
 * figure itself (display type) and this mono label after it, so this is
 * {@link formatDiscoverResultsCount} with the leading figure removed — the
 * same literal strings, never a second copy of them.
 */
export function formatDiscoverResultsCountLabel(input: DiscoverResultsCountInput): string {
  return formatDiscoverResultsCount(input).slice(formatCount(input.total).length);
}

/**
 * Accessible name for the count region. Always the full-variant wording so
 * AT is not told about a slim/full toggle it cannot see, and so a screen
 * reader never hears both CSS-hidden copies.
 */
export function formatDiscoverResultsCountAriaLabel(
  input: Omit<DiscoverResultsCountInput, 'variant'>,
): string {
  return formatDiscoverResultsCount({ ...input, variant: 'full' });
}
